import * as THREE from "three";
import { logger } from "../utils/logger";

/** Nearest-point scalar parameter along line (origin + t * dir) to an infinite ray. */
export function closestPointParamOnLineToRay(
  lineOrigin: THREE.Vector3,
  lineDir: THREE.Vector3,
  rayOrigin: THREE.Vector3,
  rayDir: THREE.Vector3
): number {
  // Solve for t on the line that minimises squared distance to the ray.
  const w0 = new THREE.Vector3().subVectors(lineOrigin, rayOrigin);
  const a = lineDir.dot(lineDir);
  const b = lineDir.dot(rayDir);
  const c = rayDir.dot(rayDir);
  const d = lineDir.dot(w0);
  const e = rayDir.dot(w0);
  const denom = a * c - b * b;
  if (Math.abs(denom) < 1e-9) {
    // Parallel - fall back to projection of rayOrigin on line.
    return -d / (a || 1);
  }
  // t along line
  return (b * e - c * d) / denom;
}

/** Compose parent world matrix by walking up the scene graph without recursion. */
export function computeParentWorldMatrix(bodyObj: THREE.Object3D): THREE.Matrix4 {
  const chain: THREE.Object3D[] = [];
  const seen = new Set<THREE.Object3D>();
  let cur: THREE.Object3D | null = bodyObj.parent || null;
  while (cur) {
    if (seen.has(cur)) {
      logger.warn("[PositionGizmo] Cyclic parent chain detected; aborting world matrix walk");
      break;
    }
    seen.add(cur);
    chain.push(cur);
    cur = cur.parent;
  }
  const world = new THREE.Matrix4().identity();
  for (let i = chain.length - 1; i >= 0; i--) {
    const n = chain[i];
    if (typeof n.updateMatrix === "function") n.updateMatrix();
    if (n.matrix) world.multiply(n.matrix);
  }
  return world;
}

/** Compose the body's own world matrix using the same non-recursive walk. */
export function computeBodyWorldMatrix(bodyObj: THREE.Object3D): THREE.Matrix4 {
  const chain: THREE.Object3D[] = [bodyObj];
  const seen = new Set<THREE.Object3D>([bodyObj]);
  let cur: THREE.Object3D | null = bodyObj.parent || null;
  while (cur) {
    if (seen.has(cur)) {
      logger.warn("[PositionGizmo] Cyclic parent chain detected; aborting world matrix walk");
      break;
    }
    seen.add(cur);
    chain.push(cur);
    cur = cur.parent;
  }
  const world = new THREE.Matrix4().identity();
  for (let i = chain.length - 1; i >= 0; i--) {
    const n = chain[i];
    if (typeof n.updateMatrix === "function") n.updateMatrix();
    if (n.matrix) world.multiply(n.matrix);
  }
  return world;
}
